import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Filter, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { labelForRow, type LedgerRow } from "../creditHistoryHelpers";
import { rowsToCsv, type LedgerRow as CsvRow } from "@/lib/creditCsv";

type FullRow = LedgerRow & { metadata: Record<string, unknown> | null };

const PAGE_SIZE = 25;
const CSV_CAP = 5000;

const KIND_OPTIONS = [
  { value: "all", label: "All kinds" },
  { value: "monthly_grant", label: "Monthly grant" },
  { value: "topup_purchase", label: "Top-up purchase" },
  { value: "consumption", label: "Used" },
  { value: "adjustment", label: "Adjustment" },
  { value: "refund", label: "Refund" },
] as const;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const CreditActivityTable = () => {
  const { user } = useAuth();
  const [kind, setKind] = useState<string>("all");
  const [job, setJob] = useState<string>("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [jobOptions, setJobOptions] = useState<string[]>([]);

  // Reset to page 0 whenever filters change.
  useEffect(() => {
    setPage(0);
  }, [kind, job, fromDate, toDate]);

  // Discover distinct job kinds for the dropdown (one-time, scoped to RLS).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("account_ai_credit_ledger")
        .select("job_kind")
        .eq("kind", "consumption")
        .not("job_kind", "is", null)
        .limit(500);
      if (cancelled) return;
      const set = new Set<string>();
      for (const r of (data ?? []) as { job_kind: string | null }[]) {
        if (r.job_kind) set.add(r.job_kind);
      }
      setJobOptions([...set].sort());
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const buildQuery = (limit: number, offset: number) => {
    let q = supabase
      .from("account_ai_credit_ledger")
      .select("id, kind, credits, balance_after, job_kind, created_at, metadata", {
        count: "exact",
      })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);
    if (kind !== "all") q = q.eq("kind", kind);
    if (job !== "all") q = q.eq("job_kind", job);
    if (fromDate) q = q.gte("created_at", new Date(fromDate).toISOString());
    if (toDate) q = q.lte("created_at", new Date(`${toDate}T23:59:59`).toISOString());
    return q;
  };

  const q = useQuery({
    queryKey: [
      "credit-activity",
      user?.id,
      kind,
      job,
      fromDate,
      toDate,
      page,
    ],
    enabled: !!user?.id,
    queryFn: async (): Promise<{ rows: FullRow[]; total: number }> => {
      const { data, count, error } = await buildQuery(PAGE_SIZE, page * PAGE_SIZE);
      if (error) throw new Error(error.message);
      return { rows: (data ?? []) as FullRow[], total: count ?? 0 };
    },
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data, error } = await buildQuery(CSV_CAP, 0);
      if (error) throw new Error(error.message);
      const csvRows: CsvRow[] = (data ?? []).map((r) => ({
        created_at: r.created_at,
        kind: r.kind,
        job_kind: r.job_kind,
        credits: r.credits,
        balance_after: r.balance_after,
        metadata: r.metadata as Record<string, unknown> | null,
      }));
      const csv = rowsToCsv(csvRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `excerptus-credits-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${csvRows.length} rows`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min((page + 1) * PAGE_SIZE, total);
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  const summary = useMemo(() => {
    if (!rows.length) return null;
    const used = rows.filter((r) => r.kind === "consumption").reduce((s, r) => s + Math.abs(r.credits), 0);
    const granted = rows.filter((r) => r.credits > 0).reduce((s, r) => s + r.credits, 0);
    return `Page totals: +${granted.toLocaleString()} granted · ${used.toLocaleString()} used`;
  }, [rows]);

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Activity log</CardTitle>
        <CardDescription>
          Every credit grant, purchase, refund, and consumption event for your
          account. Export up to {CSV_CAP.toLocaleString()} matching rows as CSV.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <Label htmlFor="ca-kind">Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger id="ca-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ca-job">Job kind</Label>
            <Select value={job} onValueChange={setJob}>
              <SelectTrigger id="ca-job"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All jobs</SelectItem>
                {jobOptions.map((j) => (
                  <SelectItem key={j} value={j}>{j.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ca-from">From</Label>
            <Input id="ca-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ca-to">To</Label>
            <Input id="ca-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void q.refetch()} disabled={q.isFetching}>
            {q.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting || rows.length === 0}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
          {summary && (
            <span className="ml-auto text-xs text-muted-foreground">{summary}</span>
          )}
        </div>

        {q.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : q.isError ? (
          <p className="text-xs text-destructive">Couldn't load activity.</p>
        ) : rows.length === 0 ? (
          <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            No activity matches these filters.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">Job</th>
                  <th className="px-3 py-2 text-right">Credits</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const positive = r.credits > 0;
                  return (
                    <tr key={r.id} className="border-t border-border/60">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                        {fmtDate(r.created_at)}
                      </td>
                      <td className="px-3 py-2">{labelForRow(r)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.job_kind ? r.job_kind.replace(/_/g, " ") : ""}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${positive ? "text-primary font-medium" : ""}`}>
                        {positive ? "+" : ""}{r.credits.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                        {r.balance_after.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total === 0
              ? "0 rows"
              : `Showing ${start}–${end} of ${total.toLocaleString()}`}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!canPrev || q.isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={!canNext || q.isFetching} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
